'use client';

import { useState, useEffect, useRef } from 'react';
import { useFirestore, useAuth } from '@/firebase/index';
import { useCollection } from '@/firebase/firestore/use-collection';
import { useMemoFirebase } from '@/firebase/index';
import { collection, query, where } from 'firebase/firestore';
import {
  Loader2, ImageIcon, Trash2, Check, X, Mail, Send, Plus, Landmark,
  Users, FileSignature, Handshake, ArrowDownLeft, ArrowUpRight, Megaphone, AlertTriangle,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { createOrganizacionSocialPost, createSocialStory, deleteSocialPost, acceptCollaboration, rejectCollaboration, createContract, signContract, rejectContract, deleteSocialStory, sendArzobispadoCarta } from '@/lib/actions';
import { MediaUploader } from '@/components/social/MediaUploader';
import { SongPicker } from '@/components/social/SongPicker';
import { MentionTextarea } from '@/components/social/MentionTextarea';
import { Music } from 'lucide-react';
import { Organizacion, SocialPost, SocialStory, JartinetHermandad, Banda, Contract } from '@/lib/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Image from '@/components/ui/Image';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ContractCard } from '../contratos/ContractCard';

export const dynamic = 'force-dynamic';

/* ──────────────────────────────────────────────────────────────
   Forms
   ────────────────────────────────────────────────────────────── */

function CreateContractForm({ emitter, allHermandades, allBandas, onFormSuccess }: { emitter: { id: string, type: string, name: string }, allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createContract(formData);
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
      <input type="hidden" name="emitterId" value={`${emitter.type}:${emitter.id}`} />
      <div className="space-y-2">
        <Label htmlFor="concept">Concepto</Label>
        <Input id="concept" name="concept" placeholder="Ej: Acompañamiento Musical Domingo de Ramos" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="recipientId">Destinatario</Label>
        <Select name="recipientId">
          <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
          <SelectContent>
            {allHermandades.map(h => (
              <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                  {h.nombreCompleto} (Hermandad)
                </div>
              </SelectItem>
            ))}
            {allBandas.map(b => (
              <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                  {b.nombreCompleto} (Banda)
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido del Contrato</Label>
        <RichTextarea id="content" name="content" placeholder="Escribe aquí las cláusulas del contrato..." rows={10} />
        <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato.</p>
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enviar Contrato
        </Button>
      </DialogFooter>
    </form>
  );
}

function CreatePostForm({ organizaciones, allHermandades, allBandas, onFormSuccess }: { organizaciones: Organizacion[], allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createOrganizacionSocialPost(null, formData);
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      {/* Organización fija (Arzobispado) — input oculto */}
      <input type="hidden" name="organizacionId" value={organizaciones[0]?.id} />
      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input id="title" name="title" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido</Label>
        <MentionTextarea id="content" name="content" placeholder="Escribe aquí el contenido... usa @ para mencionar" />
        <p className="text-xs text-muted-foreground">Usa <strong>@</strong> para mencionar cuentas. Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link>.</p>
      </div>
      <MediaUploader label="Fotos de la publicación" max={10} />
      <div className="space-y-2">
        <Label htmlFor="collaboratorId">Invitar a colaborar (Opcional)</Label>
        <Select name="collaboratorId">
          <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No colaborar</SelectItem>
            {allHermandades.map(h => (
              <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                  {h.nombreCompleto} (Hermandad)
                </div>
              </SelectItem>
            ))}
            {allBandas.map(b => (
              <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                  {b.nombreCompleto} (Banda)
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Publicar
        </Button>
      </DialogFooter>
    </form>
  );
}

function CreateStoryForm({ organizaciones, onFormSuccess }: { organizaciones: Organizacion[], onFormSuccess: () => void }) {
  const formRef = useRef<HTMLFormElement>(null);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const organizacionId = organizaciones[0]?.id;
    if (!organizacionId) {
      toast({ title: "Error", description: "No se encontró la organización.", variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    const result = await createSocialStory(organizacionId, 'organizacion', formData);
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message || 'Ocurrió un error', variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <MediaUploader single required label="Foto de la historia" />
      <div className="space-y-2">
        <Label htmlFor="text">Texto (Opcional)</Label>
        <RichTextarea id="text" name="text" placeholder="Añade un texto a tu historia..." />
      </div>
      <SongPicker />
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Publicar Historia
        </Button>
      </DialogFooter>
    </form>
  );
}

function CreateCartaForm({ onFormSuccess }: { onFormSuccess: () => void }) {
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSend = async () => {
    setIsSubmitting(true);
    const result = await sendArzobispadoCarta(subject, content);
    if (result.success) {
      toast({ title: "Carta enviada", description: result.message });
      setSubject('');
      setContent('');
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  const canSend = subject.trim().length > 0 && content.trim().length > 0;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/8 p-4">
        <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-200/90">
          Esta carta llegará al <strong>buzón de todos los usuarios de Jartinet</strong>. Revisa bien el contenido antes de enviarla.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="carta-subject">Asunto</Label>
        <Input id="carta-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ej: Comunicado oficial del Arzobispado" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="carta-content">Contenido</Label>
        <RichTextarea id="carta-content" value={content} onChange={(e) => setContent(e.target.value)} rows={10} placeholder="Escribe aquí el mensaje..." />
        <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato.</p>
      </div>

      <div className="flex justify-end">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={!canSend || isSubmitting} className="gap-2">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar a todos
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Enviar carta a toda la comunidad?</AlertDialogTitle>
              <AlertDialogDescription>
                La carta "<strong>{subject}</strong>" se enviará al buzón de <strong>todos los usuarios de Jartinet</strong>. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleSend}>Enviar carta</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Gestión de contenido social existente
   ────────────────────────────────────────────────────────────── */

function AdminSocialManager({ posts, stories, isSubmitting, handleDeletePost, handleDeleteStory }: { posts: SocialPost[], stories: SocialStory[], isSubmitting: boolean, handleDeletePost: (postId: string) => void, handleDeleteStory: (storyId: string) => void }) {
  if (posts.length === 0 && stories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
        <ImageIcon className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm">No hay contenido publicado todavía.</p>
      </div>
    );
  }

  return (
    <Tabs defaultValue="posts" className="w-full">
      <TabsList className="grid w-full grid-cols-2 mb-2">
        <TabsTrigger value="posts">Publicaciones ({posts.length})</TabsTrigger>
        <TabsTrigger value="stories">Historias ({stories.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="posts" className="space-y-3 pt-2">
        {posts.length > 0 ? posts.map(post => (
          <div key={post.id} className="flex items-start gap-4 p-3 rounded-2xl border border-white/8 bg-card/40">
            <Image
              src={Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl || ''}
              alt={post.title}
              width={80}
              height={80}
              className="rounded-xl aspect-square object-cover"
            />
            <div className="flex-grow min-w-0">
              <p className="font-semibold truncate">{post.title}</p>
              <p className="text-xs text-muted-foreground">Por: {post.authorName}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive flex-shrink-0" disabled={isSubmitting}><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar esta publicación?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleDeletePost(post.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )) : <p className="text-muted-foreground text-sm text-center py-4">No hay publicaciones.</p>}
      </TabsContent>
      <TabsContent value="stories" className="space-y-2 pt-2">
        {stories.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {stories.map(story => (
              <div key={story.id} className="relative aspect-[9/16] rounded-xl overflow-hidden group">
                <Image src={Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl} alt="Historia" className="object-cover w-full h-full" />
                <div className="absolute inset-0 bg-black/50 flex flex-col items-start justify-between p-2">
                  <p className="text-white text-xs font-bold">{story.authorName}</p>
                  <p className="text-white text-xs">Expira en {Math.round((new Date(story.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))}h</p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="destructive" className="absolute bottom-2 right-2 h-7 w-7" disabled={isSubmitting}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar esta historia?</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteStory(story.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-muted-foreground text-sm text-center py-4">No hay historias activas.</p>}
      </TabsContent>
    </Tabs>
  );
}

/* ──────────────────────────────────────────────────────────────
   Página principal
   ────────────────────────────────────────────────────────────── */

const TAB_DEFS = [
  { value: 'social', label: 'Social', icon: Users },
  { value: 'contracts', label: 'Contratos', icon: FileSignature },
  { value: 'collaborations', label: 'Colaboraciones', icon: Handshake },
  { value: 'cartas', label: 'Cartas', icon: Mail },
] as const;

export function ArzobispadoClientPage({ isAuthorized, initialArzobispado, allHermandades, allBandas, allPosts, allStories }: any) {
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isCreateContractOpen, setIsCreateContractOpen] = useState(false);
  const firestore = useFirestore();

  useEffect(() => {
    if (!isAuthorized) {
      toast({ title: 'Acceso Denegado', description: 'No tienes permisos para acceder al panel del Arzobispado.', variant: 'destructive' });
      navigate('/jartinet');
    }
  }, [isAuthorized, navigate, toast]);

  const arzobispadoCompositeId = `organizacion:${initialArzobispado?.id}`;

  const contractsSentQuery = useMemoFirebase(() => {
    if (!firestore || !initialArzobispado) return null;
    return query(collection(firestore, 'contracts'), where('emitterId', '==', arzobispadoCompositeId));
  }, [firestore, initialArzobispado, arzobispadoCompositeId]);

  const contractsReceivedQuery = useMemoFirebase(() => {
    if (!firestore || !initialArzobispado) return null;
    return query(collection(firestore, 'contracts'), where('recipientId', '==', arzobispadoCompositeId));
  }, [firestore, initialArzobispado, arzobispadoCompositeId]);

  const { data: sentContracts, isLoading: isLoadingSent } = useCollection<Contract>(contractsSentQuery);
  const { data: receivedContracts, isLoading: isLoadingReceived } = useCollection<Contract>(contractsReceivedQuery);

  if (!isAuthorized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="ml-4">Redirigiendo...</p>
      </div>
    );
  }

  const handleActionCompletion = () => refetch();

  const handleGenericAction = async (action: () => Promise<any>) => {
    setIsSubmitting(true);
    try {
      const result = await action();
      if (result.success) {
        toast({ title: "Éxito", description: result.message });
        handleActionCompletion();
      } else {
        toast({ title: "Error", description: result.error || result.message, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Error Inesperado", description: e.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = (postId: string) => {
    if (!initialArzobispado) return;
    handleGenericAction(() => deleteSocialPost(postId, initialArzobispado.id, 'organizacion'));
  };
  const handleDeleteStory = (storyId: string) => handleGenericAction(() => deleteSocialStory(storyId));

  const handleCollaborationAction = async (action: 'accept' | 'reject', postId: string) => {
    const actionFunc = action === 'accept' ? acceptCollaboration : rejectCollaboration;
    await handleGenericAction(() => actionFunc(postId));
  };

  const handleSignContract = (contractId: string) => handleGenericAction(() => signContract(contractId, arzobispadoCompositeId));
  const handleRejectContract = (contractId: string) => handleGenericAction(() => rejectContract(contractId, arzobispadoCompositeId));

  const arzobispadoPosts = (allPosts || []).filter((p: SocialPost) => p.authorId === initialArzobispado.id);
  const arzobispadoStories = (allStories || []).filter((s: SocialStory) => s.authorId === initialArzobispado.id);
  const pendingCollaborations = (allPosts || []).filter((p: SocialPost) => p.collaboratorId === arzobispadoCompositeId && p.collaborationStatus === 'pending');

  const tabBadges: Record<string, number> = {
    collaborations: pendingCollaborations.length,
    contracts: (receivedContracts || []).length,
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── Hero ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-violet-600/20 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-violet-500/25 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-fuchsia-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-violet-500/40 blur-xl" />
            <div className="relative grid h-16 w-16 place-items-center overflow-hidden rounded-2xl border border-violet-300/30 bg-violet-500/15 text-violet-200">
              {initialArzobispado?.escudoUrl
                ? <img src={initialArzobispado.escudoUrl} alt="Arzobispado" className="h-full w-full object-cover" />
                : <Landmark className="h-9 w-9" />}
            </div>
          </div>
          <div className="min-w-0">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.25em] text-violet-300/80">Panel oficial</p>
            <h1 className="font-headline text-4xl font-bold text-foreground md:text-5xl">Arzobispado</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Gestiona el contenido social, los contratos, las colaboraciones y las cartas oficiales del Arzobispado.
            </p>
          </div>
        </div>
      </motion.div>

      <Tabs defaultValue="social" className="w-full">
        {/* ── Tab bar moderna ── */}
        <TabsList className="flex h-auto w-full flex-wrap gap-1 rounded-2xl border border-white/8 bg-card/40 p-1.5">
          {TAB_DEFS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="group relative flex-1 gap-2 rounded-xl px-3 py-2.5 text-sm data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-200 data-[state=active]:shadow-[0_0_16px_rgba(139,92,246,0.15)]"
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{label}</span>
              {tabBadges[value] > 0 && (
                <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-500 px-1 text-[10px] font-bold text-white">
                  {tabBadges[value]}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── SOCIAL ── */}
        <TabsContent value="social" className="space-y-6 pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <button className="group flex items-center gap-4 rounded-2xl border border-white/8 bg-gradient-to-br from-violet-500/12 to-card/40 p-5 text-left transition-all hover:border-violet-400/30 hover:shadow-lg">
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-violet-400/30 bg-violet-500/15 text-violet-300 transition-transform group-hover:scale-110">
                    <Plus className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Crear Publicación</p>
                    <p className="text-xs text-muted-foreground">Publica en el feed social</p>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Crear Publicación</DialogTitle>
                  <DialogDescription>Publica en nombre del Arzobispado.</DialogDescription>
                </DialogHeader>
                <CreatePostForm organizaciones={[initialArzobispado]} allHermandades={allHermandades} allBandas={allBandas} onFormSuccess={handleActionCompletion} />
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <button className="group flex items-center gap-4 rounded-2xl border border-white/8 bg-gradient-to-br from-fuchsia-500/12 to-card/40 p-5 text-left transition-all hover:border-fuchsia-400/30 hover:shadow-lg">
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/15 text-fuchsia-300 transition-transform group-hover:scale-110">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Crear Historia</p>
                    <p className="text-xs text-muted-foreground">Una historia que dura 24h</p>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Historia</DialogTitle>
                  <DialogDescription>Publica una historia que durará 24h.</DialogDescription>
                </DialogHeader>
                <CreateStoryForm organizaciones={[initialArzobispado]} onFormSuccess={handleActionCompletion} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="rounded-2xl border border-white/8 bg-card/40 p-5 md:p-6">
            <h3 className="font-headline text-lg font-bold mb-4">Gestionar contenido existente</h3>
            <AdminSocialManager
              posts={arzobispadoPosts}
              stories={arzobispadoStories}
              isSubmitting={isSubmitting}
              handleDeletePost={handleDeletePost}
              handleDeleteStory={handleDeleteStory}
            />
          </div>
        </TabsContent>

        {/* ── CONTRATOS ── */}
        <TabsContent value="contracts" className="space-y-5 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl border border-sky-400/30 bg-sky-500/12 text-sky-300">
                <FileSignature className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-headline text-2xl font-bold">Contratos</h3>
                <p className="text-sm text-muted-foreground">Emite y firma contratos con hermandades y bandas.</p>
              </div>
            </div>
            <Dialog open={isCreateContractOpen} onOpenChange={setIsCreateContractOpen}>
              <DialogTrigger asChild><Button className="gap-2"><Plus className="h-4 w-4" /> Crear Contrato</Button></DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Emitir Nuevo Contrato</DialogTitle>
                  <DialogDescription>Redacta un contrato para enviarlo a una hermandad o banda.</DialogDescription>
                </DialogHeader>
                <CreateContractForm
                  emitter={{ id: initialArzobispado.id, type: 'organizacion', name: initialArzobispado.nombreCompleto }}
                  allHermandades={allHermandades}
                  allBandas={allBandas}
                  onFormSuccess={() => { handleActionCompletion(); setIsCreateContractOpen(false); }}
                />
              </DialogContent>
            </Dialog>
          </div>

          {isLoadingSent || isLoadingReceived ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              <ContractColumn
                title="Recibidos" icon={ArrowDownLeft} accent="#34d399"
                contracts={receivedContracts || []} perspectiveField="emitterName" perspectiveLabel="De"
                onSelect={setSelectedContract} emptyText="No has recibido contratos."
              />
              <ContractColumn
                title="Enviados" icon={ArrowUpRight} accent="#38bdf8"
                contracts={sentContracts || []} perspectiveField="recipientName" perspectiveLabel="Para"
                onSelect={setSelectedContract} emptyText="No has enviado contratos."
              />
            </div>
          )}
        </TabsContent>

        {/* ── COLABORACIONES ── */}
        <TabsContent value="collaborations" className="space-y-4 pt-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl border border-amber-400/30 bg-amber-500/12 text-amber-300">
              <Handshake className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-headline text-2xl font-bold">Invitaciones de Colaboración</h3>
              <p className="text-sm text-muted-foreground">Otros perfiles te invitan a co-publicar.</p>
            </div>
          </div>

          {pendingCollaborations.length > 0 ? (
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {pendingCollaborations.map((post: SocialPost) => (
                  <motion.div
                    key={post.id}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/8 bg-card/40 p-3.5"
                  >
                    <div className="flex items-center gap-3 flex-grow overflow-hidden">
                      <Avatar className="h-10 w-10 border border-white/10"><AvatarImage src={post.authorShieldUrl} /><AvatarFallback>{post.authorName?.charAt(0)}</AvatarFallback></Avatar>
                      <div className="truncate">
                        <p className="font-semibold truncate">{post.authorName}</p>
                        <p className="text-xs text-muted-foreground truncate">Te ha invitado a colaborar en: "{post.title}"</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:bg-destructive/10" onClick={() => handleCollaborationAction('reject', post.id)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                      <Button size="icon" className="h-9 w-9 bg-emerald-500 hover:bg-emerald-600" onClick={() => handleCollaborationAction('accept', post.id)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-14 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
              <Handshake className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">No hay invitaciones de colaboración pendientes.</p>
            </div>
          )}
        </TabsContent>

        {/* ── CARTAS ── */}
        <TabsContent value="cartas" className="space-y-4 pt-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl border border-primary/30 bg-primary/12 text-primary">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-headline text-2xl font-bold">Carta a la comunidad</h3>
              <p className="text-sm text-muted-foreground">Envía una carta oficial al buzón de todos los usuarios.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-white/8 bg-card/40 p-5 md:p-6">
            <CreateCartaForm onFormSuccess={handleActionCompletion} />
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Contract detail dialog ── */}
      {selectedContract && (
        <Dialog open={!!selectedContract} onOpenChange={(open) => !open && setSelectedContract(null)}>
          <DialogContent className="w-full max-w-md h-auto max-h-[90vh] p-0">
            <ContractCard contract={selectedContract} perspective="recipient" isDetailView />
            {selectedContract.recipientId === arzobispadoCompositeId && selectedContract.status === 'pending' && (
              <DialogFooter className="border-t p-4">
                <Button variant="destructive" onClick={() => { handleRejectContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Rechazar</Button>
                <Button onClick={() => { handleSignContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Firmar Contrato</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ContractColumn({ title, icon: Icon, accent, contracts, perspectiveField, perspectiveLabel, onSelect, emptyText }: {
  title: string; icon: React.ElementType; accent: string;
  contracts: Contract[]; perspectiveField: 'emitterName' | 'recipientName'; perspectiveLabel: string;
  onSelect: (c: Contract) => void; emptyText: string;
}) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h4 className="font-semibold">{title}</h4>
        <span className="ml-auto text-xs text-muted-foreground">{contracts.length}</span>
      </div>
      <div className="space-y-2">
        {contracts.map(contract => (
          <button
            key={contract.id}
            onClick={() => onSelect(contract)}
            className="w-full text-left p-3 rounded-xl border border-white/6 bg-muted/20 hover:bg-muted/40 hover:border-white/15 transition-colors"
          >
            <p className="font-semibold text-sm truncate">{contract.concept}</p>
            <p className="text-xs text-muted-foreground truncate">{perspectiveLabel}: {contract[perspectiveField]}</p>
            <p className={`mt-1 text-[0.65rem] font-bold uppercase tracking-wider ${contract.status === 'signed' ? 'text-emerald-400' : contract.status === 'rejected' ? 'text-destructive' : 'text-amber-400'}`}>
              {contract.status}
            </p>
          </button>
        ))}
        {contracts.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{emptyText}</p>}
      </div>
    </div>
  );
}
